import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { MapPin, X } from 'lucide-react';

import { useAppDispatch } from '../../hooks';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { DrawingDocument, fromDrawingDocument } from '../new-project-cad/drawingDocument';
import { DrawingExample, EXAMPLES_URL, useDrawingExamples } from './drawingExamples';
import { drawingsApi } from './drawingsApi';
import { renderPlanThumbnail } from './planThumbnail';

/**
 * Prepared drawings of real streets (traced from OpenStreetMap). Choosing one makes the user's own
 * copy, which then opens in the editor like any other drawing.
 */
export function DrawingExamplesDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const examples = useDrawingExamples();
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !opening && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, opening]);

  const open = async (example: DrawingExample) => {
    setOpening(example.id);
    try {
      const response = await fetch(EXAMPLES_URL + example.file);
      if (!response.ok) throw new Error(String(response.status));
      const document = (await response.json()) as DrawingDocument;
      const drawing = await drawingsApi.create(example.name, document);
      // The list shows the copy with its picture straight away, even if the editor is left untouched.
      const loaded = fromDrawingDocument(document);
      if (loaded) {
        drawingsApi.savePreview(drawing.id, renderPlanThumbnail(loaded.polygons, 640)).catch(() => undefined);
      }
      navigate(`/drawings/${drawing.id}`);
    } catch {
      setOpening(null);
      dispatch(toastReducer.actions.showToast({ message: 'drawings.examples.failed', type: 'error' }));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={() => !opening && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawing-examples-title"
        className="w-full max-w-[920px] max-h-[90vh] overflow-y-auto custom-scrollbar rounded-xl bg-greyP5 border border-white/10 p-6 md:p-8 text-gray-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="drawing-examples-title" className="text-white text-xl font-medium">
              {t('drawings.examples.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-400">{t('drawings.examples.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            disabled={!!opening}
            aria-label={t('common.close')}
            className="text-gray-400 hover:text-white disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {examples === null ? (
          <p className="py-12 text-center text-sm text-gray-500">{t('common.loading')}</p>
        ) : examples === 'failed' || examples.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">{t('drawings.examples.none')}</p>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {examples.map((example) => (
              <button
                key={example.id}
                onClick={() => open(example)}
                disabled={!!opening}
                className="group flex flex-col text-left rounded-xl border border-greyP4 bg-black/50 overflow-hidden hover:border-lime-400/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-lime-400 disabled:opacity-60 transition-colors"
              >
                <div className="aspect-[4/3] bg-black flex items-center justify-center border-b border-greyP4">
                  <img
                    src={EXAMPLES_URL + example.preview}
                    alt=""
                    className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="p-4 flex flex-col gap-1">
                  <span className="text-white font-medium">{example.name}</span>
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <MapPin className="w-3.5 h-3.5" /> {example.place}
                  </span>
                  <span className="text-xs text-gray-500">
                    {t('drawings.polygonCount', { count: example.polygons })}
                  </span>
                  <span className="mt-2 text-sm text-lime-400">
                    {opening === example.id ? t('drawings.examples.opening') : t('drawings.examples.use')}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500">{t('drawings.examples.attribution')}</p>
      </div>
    </div>
  );
}
